import { useEffect, useRef, useState } from 'react';
import { RedeemConsentController } from './redeemConsentController';
import { canResend, secondsUntil } from './redeemConsentPresentation';
/** React binding for RedeemConsentController (polling + a 1 s clock for the countdowns). */
export function useRedeemConsent(options) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const [controller] = useState(() => new RedeemConsentController({
        api: options.api,
        initialRequest: options.initialRequest,
        onApproved: (request, redeem) => { var _a, _b; return (_b = (_a = optionsRef.current).onApproved) === null || _b === void 0 ? void 0 : _b.call(_a, request, redeem); },
        onRequestChange: (request) => { var _a, _b; return (_b = (_a = optionsRef.current).onRequestChange) === null || _b === void 0 ? void 0 : _b.call(_a, request); },
    }));
    const [state, setState] = useState(controller.getState());
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const unsubscribe = controller.subscribe(() => setState(controller.getState()));
        controller.start();
        return () => {
            unsubscribe();
            controller.stop();
        };
    }, [controller]);
    const pending = state.request.status === 'pending';
    useEffect(() => {
        if (!pending)
            return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [pending]);
    const expiresIn = secondsUntil(state.request.expires_at, now);
    const refreshedAtExpiry = useRef(false);
    useEffect(() => {
        // The server decides expiry; ask once when the local countdown reaches zero.
        if (pending && expiresIn === 0 && !refreshedAtExpiry.current) {
            refreshedAtExpiry.current = true;
            void controller.refresh();
        }
    }, [pending, expiresIn, controller]);
    return {
        ...state,
        expiresIn,
        resendIn: secondsUntil(state.request.resend.next_resend_at, now),
        canResend: canResend(state.request, now),
        setCode: (value) => controller.setCode(value),
        verify: () => controller.verify(),
        resend: () => controller.resend(),
        cancel: () => controller.cancel(),
    };
}
